/**
 * CollectionActions - Premium Design System v2.0
 * Actions inline para rows de tabela CRUD
 *
 * Features:
 * - Dark/Light mode via tokens
 * - Mobile: menu dropdown
 * - Desktop: inline icons com tooltips
 * - Visible Danger principle (delete sempre vermelho)
 * - Smooth animations e hover states
 */

import { useState, memo, useMemo } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Stack,
  Tooltip,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import RemoveDialog from '../../common/components/RemoveDialog';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

const CollectionActions = memo(({
  itemId,
  editPath,
  endpoint,
  setTimestamp,
  customActions,
  readonly,
  compact = false,
}) => {
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const navigate = useNavigate();
  const t = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Handlers
  const handleEdit = () => {
    navigate(`${editPath}/${itemId}`);
    setMenuAnchor(null);
  };

  const handleRemove = () => {
    setRemoving(true);
    setMenuAnchor(null);
  };

  const handleCustom = (action) => {
    action.handler(itemId);
    setMenuAnchor(null);
  };

  const handleRemoveResult = (removed) => {
    setRemoving(false);
    if (removed) setTimestamp(Date.now());
  };

  // Icon size based on compact mode - INCREASED for better visibility
  const iconFontSize = compact ? 'small' : 'medium';
  const iconSize = compact ? 20 : 24;

  // Styles - Todos baseados em tokens com cores diversificadas
  const styles = useMemo(() => ({
    // Base para todos os icon buttons
    iconBase: {
      p: compact ? 0.75 : 1,
      borderRadius: tokens?.radius?.md,
      transition: tokens?.transitions?.all,
    },
    // Edit - Accent color (verde/teal)
    iconEdit: {
      color: tokens?.accent?.primary,
      opacity: 0.85,
      '&:hover': {
        color: tokens?.accent?.primary,
        bgcolor: tokens?.accent?.subtle,
        opacity: 1,
        transform: 'scale(1.15)',
      },
    },
    // Info (connections, custom actions) - Azul
    iconInfo: {
      color: tokens?.semantic?.info || '#58a6ff',
      opacity: 0.85,
      '&:hover': {
        color: tokens?.semantic?.info || '#58a6ff',
        bgcolor: tokens?.semantic?.infoBg || 'rgba(88, 166, 255, 0.15)',
        opacity: 1,
        transform: 'scale(1.15)',
      },
    },
    // Warning (link, connections) - Amarelo/Laranja
    iconWarning: {
      color: tokens?.semantic?.warning || '#d29922',
      opacity: 0.85,
      '&:hover': {
        color: tokens?.semantic?.warning || '#d29922',
        bgcolor: tokens?.semantic?.warningBg || 'rgba(210, 153, 34, 0.15)',
        opacity: 1,
        transform: 'scale(1.15)',
      },
    },
    // Danger (delete) - Vermelho
    iconDanger: {
      color: tokens?.semantic?.error || '#f85149',
      opacity: 0.8,
      '&:hover': {
        color: tokens?.semantic?.error || '#f85149',
        bgcolor: tokens?.semantic?.errorBg || 'rgba(248, 81, 73, 0.15)',
        opacity: 1,
        transform: 'scale(1.15)',
      },
    },
    // Menu container
    menuPaper: {
      bgcolor: tokens?.surface?.overlay,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      border: `1px solid ${tokens?.border?.subtle}`,
      borderRadius: tokens?.radius?.lg,
      minWidth: 180,
      mt: 1,
      boxShadow: tokens?.shadows?.lg,
      '& .MuiList-root': { p: 1 },
    },
    // Menu item base
    menuItem: {
      py: 1.25,
      px: 1.75,
      borderRadius: tokens?.radius?.md,
      gap: 1.5,
      transition: tokens?.transitions?.all,
      color: tokens?.text?.primary,
      '&:hover': {
        bgcolor: tokens?.accent?.subtle,
        color: tokens?.accent?.primary,
        '& .MuiListItemIcon-root': { color: tokens?.accent?.primary },
      },
    },
    // Menu item danger
    menuItemDanger: {
      py: 1.25,
      px: 1.75,
      borderRadius: tokens?.radius?.md,
      gap: 1.5,
      transition: tokens?.transitions?.all,
      color: tokens?.text?.primary,
      '&:hover': {
        bgcolor: tokens?.semantic?.errorBg,
        color: tokens?.semantic?.error,
        '& .MuiListItemIcon-root': { color: tokens?.semantic?.error },
      },
    },
  }), [tokens, compact]);

  // Mobile Menu
  if (isMobile) {
    return (
      <>
        <Tooltip title={t('sharedActions')} arrow>
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ ...styles.iconBase, ...styles.iconEdit }}
          >
            <MoreVertIcon sx={{ fontSize: iconSize }} />
          </IconButton>
        </Tooltip>

        <Menu
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: styles.menuPaper } }}
        >
          {/* Custom Actions */}
          {customActions?.map((action) => (
            <MenuItem
              key={action.key}
              onClick={() => handleCustom(action)}
              sx={styles.menuItem}
            >
              {action.icon && (
                <ListItemIcon sx={{ color: action.color || tokens?.semantic?.info || '#58a6ff', minWidth: 40 }}>
                  {action.icon}
                </ListItemIcon>
              )}
              <ListItemText primary={action.title} />
            </MenuItem>
          ))}

          {/* Edit */}
          {!readonly && editPath && (
            <MenuItem key="edit" onClick={handleEdit} sx={styles.menuItem}>
              <ListItemIcon sx={{ color: tokens?.accent?.primary, minWidth: 40 }}>
                <EditIcon />
              </ListItemIcon>
              <ListItemText primary={t('sharedEdit')} />
            </MenuItem>
          )}

          {/* Delete */}
          {!readonly && (
            <MenuItem key="remove" onClick={handleRemove} sx={styles.menuItemDanger}>
              <ListItemIcon sx={{ color: tokens?.semantic?.error || '#f85149', minWidth: 40 }}>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText primary={t('sharedRemove')} />
            </MenuItem>
          )}
        </Menu>

        <RemoveDialog
          open={removing}
          endpoint={endpoint}
          itemId={itemId}
          onResult={handleRemoveResult}
        />
      </>
    );
  }

  // Desktop Inline Actions
  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={compact ? 0.5 : 1}>
        {/* Custom Actions - support custom colors via action.variant */}
        {customActions?.map((action) => {
          const variantStyle = action.variant === 'warning' ? styles.iconWarning
            : action.variant === 'danger' ? styles.iconDanger
            : styles.iconInfo;
          const variantClass = action.variant === 'warning' ? 'action-warning'
            : action.variant === 'danger' ? 'action-danger'
            : 'action-info';
          return (
            <Tooltip title={action.title} key={action.key} arrow>
              <IconButton
                size="small"
                className={`collection-action-btn ${variantClass}`}
                onClick={() => handleCustom(action)}
                sx={{ ...styles.iconBase, ...variantStyle, '& .MuiSvgIcon-root': { fontSize: iconSize } }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          );
        })}

        {/* Edit */}
        {!readonly && editPath && (
          <Tooltip title={t('sharedEdit')} arrow>
            <IconButton
              size="small"
              className="collection-action-btn action-edit"
              onClick={handleEdit}
              sx={{ ...styles.iconBase, ...styles.iconEdit }}
            >
              <EditIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Delete */}
        {!readonly && (
          <Tooltip title={t('sharedRemove')} arrow>
            <IconButton
              size="small"
              className="collection-action-btn action-danger"
              onClick={handleRemove}
              sx={{ ...styles.iconBase, ...styles.iconDanger }}
            >
              <DeleteIcon sx={{ fontSize: iconSize }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <RemoveDialog
        open={removing}
        endpoint={endpoint}
        itemId={itemId}
        onResult={handleRemoveResult}
      />
    </>
  );
});

CollectionActions.displayName = 'CollectionActions';

export default CollectionActions;
